/**
 * Wording and value shapes of a reservation — shared by the list, the modal and (later) the calendar.
 */

import type { FleetEntry, Rezervace, RucniSleva } from './api';
import type { EventChipTone } from '@/components/EventChip';
import { formatInt } from '@/lib/format';
import { pragueNowLocal } from '@/lib/prague-date';
import { stejnaSleva } from './ceny-helpers';

export type RezervaceTyp = Rezervace['typ'];
export type RezervaceStredisko = Rezervace['stredisko'];

export const TYP_LABEL: Record<RezervaceTyp, string> = {
  denni: 'Denní',
  technicky: 'Technický',
  servis: 'Servis',
};

/** An internal block of a car (technical, or the workshop's): no customer, no price, no drivers. A workshop block is also never handed over. */
export const jeBlokace = (typ: RezervaceTyp): boolean => typ === 'technicky' || typ === 'servis';

export const stredisko: Array<{ value: RezervaceStredisko; label: string }> = [
  { value: 'pujcovna', label: 'Půjčovna' },
  { value: 'servis_servis', label: 'Servis — hradí servis' },
  { value: 'servis_pojistovna', label: 'Servis — hradí pojišťovna' },
  { value: 'zamestnanci', label: 'Zaměstnanci' },
];

export const STREDISKO_LABEL: Record<RezervaceStredisko, string> = Object.fromEntries(
  stredisko.map((s) => [s.value, s.label]),
) as Record<RezervaceStredisko, string>;

/**
 * The colour of a calendar bar — what it IS, not when: red when the car is out past its return or in the workshop (`servis`: the car is not
 * rentable, the bar says "Servis"), grey for every technical block (whatever its středisko), otherwise by středisko — the rental's own green, servis blue (whoever pays for it), employees amber (paid internally).
 * A reservation that has ended keeps its colour (`jeUkoncena` fades it).
 */
export function tonRezervace(r: Pick<Rezervace, 'typ' | 'stredisko' | 'po_terminu'>): EventChipTone {
  if (r.po_terminu || r.typ === 'servis') return 'danger';
  if (r.typ === 'technicky') return 'neutral';
  const servis = r.stredisko === 'servis_servis' || r.stredisko === 'servis_pojistovna';
  return servis ? 'accent' : r.stredisko === 'zamestnanci' ? 'warn' : 'ok';
}

/**
 * What a car's card names from its active reservations that are not over (`nyni` = Prague's `YYYY-MM-DD HH:mm`): the CURRENT one — the
 * car is out with it (also past its end), else the one whose term covers now and is waiting for its handover — and the NEXT one, the
 * earliest that has not started. A returned one is over.
 */
export function aktualniRezervace<T extends Pick<Rezervace, 'od' | 'do' | 'predano_at' | 'vraceno_at'>>(rows: readonly T[], nyni: string): { aktualni: T | null; dalsi: T | null } {
  const zive = rows.filter((r) => !r.vraceno_at);
  const venku = zive.find((r) => r.predano_at);
  const ceka = zive.filter((r) => !r.predano_at && r.od.slice(0, 16) <= nyni && r.do.slice(0, 16) >= nyni).sort((a, b) => a.od.localeCompare(b.od))[0];
  const dalsi = zive.filter((r) => !r.predano_at && r.od.slice(0, 16) > nyni).sort((a, b) => a.od.localeCompare(b.od))[0];
  return { aktualni: venku ?? ceka ?? null, dalsi: dalsi ?? null };
}

/** The reservation is over (its end is before today, Prague) — drawn faded in its own colour. A car still out past its end is not over. */
export const jeUkoncena = (r: Pick<Rezervace, 'do' | 'po_terminu'>, today: string): boolean => !r.po_terminu && r.do.slice(0, 10) < today;

/**
 * The second line of a calendar bar — what the employee handing the car over wants at a glance: the renter's phone, the price to
 * settle (rent + deposit, as frozen with the reservation) and the hours of the pickup and the return (the days are the bar itself).
 * A technical block has no renter or price, so it says whose block it is; a rental of another středisko than the rental's own says which.
 */
export function podtitulekRezervace(r: Pick<Rezervace, 'typ' | 'stredisko' | 'od' | 'do' | 'zakaznik_telefon' | 'cena'>): string {
  const hodiny = `${r.od.slice(11, 16)} → ${r.do.slice(11, 16)}`;
  if (r.typ === 'servis') return `V servisu · ${hodiny}`;
  if (r.typ === 'technicky') return `${STREDISKO_LABEL[r.stredisko]} · ${hodiny}`;
  // a středisko other than the rental's own (employees, servis) is named: it says who pays, and explains the bar's colour
  const hradi = r.stredisko === 'pujcovna' ? null : STREDISKO_LABEL[r.stredisko];
  return [hradi, r.zakaznik_telefon, r.cena ? `${formatInt(r.cena.celkem)} Kč` : null, hodiny].filter(Boolean).join(' · ');
}

/**
 * The středisko a form saves. A technical block (or a workshop one) has none to choose (nothing is priced, the legacy form offered none): a new one is the
 * rental's own, and one that already is technical keeps what it has (a handed-over block's středisko is frozen, a servis replacement
 * car's says who pays). A rental saves the chosen one.
 */
export function strediskoKUlozeni(
  typ: RezervaceTyp,
  vybrane: RezervaceStredisko,
  puvodni: Pick<Rezervace, 'typ' | 'stredisko'> | null,
): RezervaceStredisko {
  if (!jeBlokace(typ)) return vybrane;
  return puvodni && jeBlokace(puvodni.typ) ? puvodni.stredisko : 'pujcovna';
}

/** The API's `YYYY-MM-DD HH:mm` → a `datetime-local` input's `YYYY-MM-DDTHH:mm`. */
export const toInputValue = (v: string): string => v.replace(' ', 'T').slice(0, 16);

/** A `datetime-local` value → the API's `YYYY-MM-DD HH:mm`. */
export const fromInputValue = (v: string): string => v.replace('T', ' ').slice(0, 16);

/** A stored instant (an ISO timestamp such as the cancellation moment) in Prague time as `10. 11. 2026 10:00`; '' when it is not a date. */
export function formatCasPraha(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : formatDateTime(pragueNowLocal(d));
}

/** `2026-11-10 10:00` → `10. 11. 2026 10:00`. */
export function formatDateTime(v: string): string {
  const [d, t] = v.split(' ');
  const date = d.split('-').reverse().map(Number).join('. ');
  return t ? `${date} ${t}` : date;
}

/** `10. 11. 2026 10:00 – 12. 11. 2026 10:00`. */
export const formatTermin = (od: string, do_: string): string => `${formatDateTime(od)} – ${formatDateTime(do_)}`;

/** Whole calendar days a reservation spans (pickup and return day both counted). */
export function dnuRezervace(od: string, do_: string): number {
  const a = Date.parse(`${od.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${do_.slice(0, 10)}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000) + 1;
}

/**
 * The order of the cars in the calendar: by what the car IS (make and model), then by plate — so every Kuga sits together instead
 * of the cars being scattered by their plates. Czech collation, numbers by value; a car without a name sorts by its plate alone.
 */
export function porovnejVozy(a: { nazev: string; spz: string }, b: { nazev: string; spz: string }): number {
  const o = { numeric: true, sensitivity: 'base' } as const;
  // a car whose stock record cannot be read has no name: it goes after the named ones, by its plate / VIN
  if ((a.nazev === '') !== (b.nazev === '')) return a.nazev === '' ? 1 : -1;
  return a.nazev.localeCompare(b.nazev, 'cs', o) || a.spz.localeCompare(b.spz, 'cs', o);
}

/** The fleet in the order the calendar and the Vozidla list share: make and model, then plate (else VIN). */
export const seradFlotilu = (fleet: readonly FleetEntry[]): FleetEntry[] =>
  [...fleet].sort((a, b) =>
    porovnejVozy(
      { nazev: [a.vozidlo?.znacka_nazev, a.vozidlo?.model_nazev].filter(Boolean).join(' '), spz: a.vozidlo?.spz || a.vin },
      { nazev: [b.vozidlo?.znacka_nazev, b.vozidlo?.model_nazev].filter(Boolean).join(' '), spz: b.vozidlo?.spz || b.vin },
    ),
  );

/** What a car is called in a picker: the plate (else the VIN) and the model. */
export function vozidloLabel(f: FleetEntry): string {
  const v = f.vozidlo;
  const name = v ? [v.znacka_nazev, v.model_nazev].filter(Boolean).join(' ') : '';
  return [v?.spz || f.vin, name].filter(Boolean).join(' · ');
}

// ---------- Calendar (car × day, one month) ----------

/** `YYYY-MM` of a `YYYY-MM-DD` day. */
export const monthOf = (day: string): string => day.slice(0, 7);

/** `YYYY-MM` + n months. */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** The days of a month as `YYYY-MM-DD`, in order. */
export function daysOfMonth(month: string): string[] {
  const [y, m] = month.split('-').map(Number);
  const count = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: count }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}

const MONTHS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];

/** `2026-11` → `listopad 2026`. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

/** 0 = Monday … 6 = Sunday for a `YYYY-MM-DD` day. */
export function weekdayOf(day: string): number {
  return (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
}

export interface BarPlace {
  /** 0-based index of the first and last visible day of the month. */
  from: number;
  to: number;
  /** The reservation begins / ends outside this month (the bar is cut there). */
  cutBefore: boolean;
  cutAfter: boolean;
}

/**
 * The end a calendar bar is DRAWN to: the actual return when the car came back late, today while it is out and past
 * its scheduled end (it still occupies the car), otherwise the scheduled end. Display only — the booking guard on the
 * server uses the same idea as its effective end.
 */
export function konecProZobrazeni(r: { do: string; vraceno_at: string | null; po_terminu: boolean }, today: string): string {
  if (r.vraceno_at && r.vraceno_at > r.do) return r.vraceno_at;
  if (r.po_terminu && today > r.do.slice(0, 10)) return `${today} 23:59`;
  return r.do;
}

/** Where a reservation sits in a month's day columns; null = it does not touch the month. */
export function barPlace(r: { od: string; do: string }, days: string[]): BarPlace | null {
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const od = r.od.slice(0, 10);
  const do_ = r.do.slice(0, 10);
  if (do_ < first || od > last) return null;
  const startDay = od < first ? first : od;
  const endDay = do_ > last ? last : do_;
  return {
    from: days.indexOf(startDay),
    to: days.indexOf(endDay),
    cutBefore: od < first,
    cutAfter: do_ > last,
  };
}

/**
 * Lane per bar so overlapping reservations of one car (an admin booked through a collision, or a gap of 0 days
 * between two) stack instead of hiding each other. Greedy by start; returns the lane of each input, in order.
 */
export function assignLanes(places: Array<{ from: number; to: number }>): number[] {
  const order = places.map((_, i) => i).sort((a, b) => places[a]!.from - places[b]!.from || places[a]!.to - places[b]!.to);
  const laneEnd: number[] = [];
  const lanes = new Array<number>(places.length).fill(0);
  for (const i of order) {
    const p = places[i]!;
    let lane = laneEnd.findIndex((end) => end < p.from);
    if (lane === -1) {
      lane = laneEnd.length;
      laneEnd.push(p.to);
    } else {
      laneEnd[lane] = p.to;
    }
    lanes[i] = lane;
  }
  return lanes;
}

/** The editable state of the reservation form, as the form holds it. */
export interface RezervaceFormular {
  idVozidla: number | null;
  idPobocky: number | null;
  typ: RezervaceTyp;
  stredisko: RezervaceStredisko;
  /** `datetime-local` values. */
  od: string;
  do: string;
  idZakaznika: number | null;
  poznamka: string;
  /** The insurer (null for any other středisko). */
  idPojistovny?: number | null;
  /** The price inputs; `sleva` 'chyba' = a box that is not a number (counts as a change). */
  zahranici?: boolean;
  sleva?: RucniSleva | null | 'chyba';
  /** The deposit: null = the car's own; 'chyba' = a box that is not a whole number (counts as a change). */
  kauce?: number | null | 'chyba';
}

/** True when the form differs from the stored reservation — what a button that leaves the form must not discard. */
export function jeFormularZmenen(r: Rezervace, f: RezervaceFormular): boolean {
  return (
    f.idVozidla !== r.id_vozidla ||
    f.idPobocky !== r.id_pobocky ||
    f.typ !== r.typ ||
    f.stredisko !== r.stredisko ||
    f.od !== toInputValue(r.od) ||
    f.do !== toInputValue(r.do) ||
    (jeBlokace(f.typ) ? null : f.idZakaznika) !== (jeBlokace(r.typ) ? null : r.id_zakaznika) ||
    f.poznamka.trim() !== r.poznamka.trim() ||
    (f.idPojistovny !== undefined && f.idPojistovny !== r.id_pojistovny) ||
    (f.zahranici !== undefined && f.zahranici !== r.cena_vstup.zahranici) ||
    (f.sleva !== undefined && (f.sleva === 'chyba' || !stejnaSleva(f.sleva, r.cena_vstup.sleva))) ||
    (f.kauce !== undefined && (f.kauce === 'chyba' || f.kauce !== (r.cena_vstup.kauce ?? null)))
  );
}

/** Why the reservation form only shows a reservation (undefined = editable): no right to change it, or the car left the fleet. */
export function duvodJenCteni(canWrite: boolean, autoVeFlotile: boolean): string | undefined {
  if (!canWrite) return 'Rezervaci můžete jen prohlížet — ke změnám je potřeba právo měnit data půjčovny.';
  if (!autoVeFlotile) return 'Vozidlo už není ve vozovém parku půjčovny — rezervaci lze jen prohlížet.';
  return undefined;
}

/** The form offers "Předat vůz…" on an active reservation that is still in the yard. */
export const lzePredatZFormulare = (r: Pick<Rezervace, 'stav' | 'predano_at' | 'typ'>, canWrite: boolean) =>
  canWrite && r.stav === 'aktivni' && !r.predano_at && r.typ !== 'servis';

/** The form offers "Přijmout vozidlo…" on an active rental that is out. */
export const lzePrijmoutZFormulare = (r: Pick<Rezervace, 'stav' | 'predano_at' | 'vraceno_at'>, canWrite: boolean) =>
  canWrite && r.stav === 'aktivni' && !!r.predano_at && !r.vraceno_at;
